import React, { useCallback, useLayoutEffect, useState } from 'react';
import type {
  IDetailCompProps,
  IMasterDetailProps,
  TSelectedDetail,
} from '../../../../Types/MasterDetailTypes';
import { EDNavigationOverlay } from '../../../../BaseComponents/NavigationOverlay/EDNavigationOverlay';
import { checkTrailingSlash } from '../../../../Utils/utils';
import { NestedBreadcrumbsContextProvider } from '../../../../Features/Delsystem/_Common/Components/RutinBreadcrumbs/BreadcrumbsContext';
import { definedSelectedId, NewId, selectedId } from '../../MasterDetailUtils';
import { MdDetailSelectionContextProvider } from '../../Contexts/MdDetailSelectionContext';
import type { SelectDetailType } from '../../../../Features/Delsystem/_StandardRutin/StandardRutin';
import { useDetailNavigation } from '../../Hooks/useDetailNavigation';
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useResolvedPath,
} from 'react-router';
import { IClientBusinessModel } from '../../../../Types/ClientModelTypes';

interface IProps extends IMasterDetailProps {
  detailRouteName?: string;
  selectDetail?: SelectDetailType;
  selectedDetail?: TSelectedDetail;
  controlled?: boolean;
  collection: IClientBusinessModel;
}

interface IMdModalRouteDetailProps {
  idName: string;
  selectedDetail: TSelectedDetail;
  onIdChange: (id: string | null) => void;
  handleSelectDetail: (
    detail: TSelectedDetail,
    force?: boolean,
    urlParams?: Record<string, string>
  ) => void;
  unselectDetail: () => void;
  selectNextDetail?: (force?: boolean) => void;
  selectPreviousDetail?: () => void;
  currentItem?: number;
  totalItems?: number;
  indexCaption: string;
  detail: React.ComponentType<React.PropsWithChildren<IDetailCompProps>>;
}

const detailConfig = { fullPage: true, parents: [] };

// MdModalRouteDetail must be defined at module scope (not inside MdModalRoute's render)
// to remain a stable component reference and avoid remounting on every parent render.
const MdModalRouteDetail: React.FC<IMdModalRouteDetailProps> = ({
  idName,
  selectedDetail,
  onIdChange,
  handleSelectDetail,
  unselectDetail,
  selectNextDetail,
  selectPreviousDetail,
  currentItem,
  totalItems,
  indexCaption,
  detail: Detail,
}) => {
  const params = useParams();
  const idParam = params[idName] ?? null;

  // useLayoutEffect fires synchronously before browser paint, so the outer component
  // updates activeIdParam (and thus overlay open state) without any visible flash.
  useLayoutEffect(() => {
    onIdChange(idParam);
  }, [idParam, onIdChange]);

  useLayoutEffect(() => {
    return () => onIdChange(null);
  }, [onIdChange]);

  return (
    <MdDetailSelectionContextProvider
      selectedDetail={selectedDetail}
      selectDetail={handleSelectDetail}
      unselectDetail={unselectDetail}
      selectNextDetail={selectNextDetail}
      selectPreviousDetail={selectPreviousDetail}
      currentItem={currentItem}
      totalItems={totalItems}
      indexCaption={indexCaption}
    >
      {!!selectedDetail && (
        <Detail
          id={selectedDetail}
          isNew={idParam === NewId}
          unselect={unselectDetail}
          selectOther={handleSelectDetail}
          config={detailConfig}
          key={selectedId(selectedDetail)}
        />
      )}
    </MdDetailSelectionContextProvider>
  );
};

/**
 * Master-Detail komponent som visar detaljnivån modalt.
 * Byter route (till skillnad från MdModal) när man går in på en detalj
 *
 */
export const MdModalRoute: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { selectDetail } = props;
  const detailRoute = props.detailRouteName ? `${props.detailRouteName}/` : '';
  const idName = props.detailRouteName ? `${props.detailRouteName}_id` : 'id';

  const navigate = useNavigate();
  const baseUrl = checkTrailingSlash(useResolvedPath('.').pathname);

  //selectedDetail sparas både i state och i routen, routen används som backup
  //när state inte finns t.ex. om man laddar om sidan. Routen innehåller då
  //bara id och ingen extra data
  const [selectedDetailState, setSelectedDetailState] =
    useState<TSelectedDetail>(null);

  const [activeIdParam, setActiveIdParam] = useState<string | null>(null);
  const handleIdChange = useCallback(
    (id: string | null) => setActiveIdParam(id),
    []
  );

  const selectedDetail =
    (activeIdParam &&
      (props.controlled ? props.selectedDetail : selectedDetailState)) ??
    activeIdParam;

  const unselectDetail = useCallback(() => {
    if (props.controlled && selectDetail) {
      selectDetail(null, false, () => {
        navigate(baseUrl);
      });
    } else {
      setSelectedDetailState(null);
      navigate(baseUrl);
    }
  }, [props.controlled, selectDetail, navigate, baseUrl]);

  const handleSelectDetail = useCallback(
    (
      selectedDetail: TSelectedDetail,
      force?: boolean,
      urlParams?: Record<string, string>
    ) => {
      const search = `?${new URLSearchParams(urlParams).toString()}`;

      if (props.controlled && selectDetail) {
        selectDetail(selectedDetail, !!force, () => {
          navigate(
            {
              pathname: `${baseUrl}${detailRoute}${
                definedSelectedId(selectedDetail ?? '') || ''
              }`,
              search,
            },
            {
              state: { disableOpenChoiceDialog: true },
            }
          );
        });
      } else {
        setSelectedDetailState(selectedDetail);
        if (selectedDetail !== null) {
          navigate(
            {
              pathname: `${baseUrl}${detailRoute}${
                definedSelectedId(selectedDetail) || ''
              }/`,
              search,
            },
            {
              state: selectedDetail,
            }
          );
        } else {
          unselectDetail();
        }
      }
    },
    [
      baseUrl,
      detailRoute,
      navigate,
      selectDetail,
      props.controlled,
      unselectDetail,
    ]
  );
  const Master = props.master.comp;
  const Detail = props.detail.comp;

  const {
    selectNextDetail,
    selectPreviousDetail,
    indexCaption,
    currentItem,
    totalItems,
  } = useDetailNavigation(selectedDetail, handleSelectDetail, props.collection);

  return (
    <NestedBreadcrumbsContextProvider>
      <Master
        key="master"
        selectedDetail={selectedDetail}
        selectDetail={handleSelectDetail}
        selectNextDetail={selectNextDetail}
        selectPreviousDetail={selectPreviousDetail}
      />
      <EDNavigationOverlay
        open={!!activeIdParam}
        onClose={unselectDetail}
        disableEscapeKeyDown
        keepMounted
      >
        <Routes>
          <Route
            path={`${detailRoute}:${idName}`}
            element={
              <MdModalRouteDetail
                idName={idName}
                selectedDetail={selectedDetail}
                onIdChange={handleIdChange}
                handleSelectDetail={handleSelectDetail}
                unselectDetail={unselectDetail}
                selectNextDetail={selectNextDetail}
                selectPreviousDetail={selectPreviousDetail}
                currentItem={currentItem}
                totalItems={totalItems}
                indexCaption={indexCaption}
                detail={Detail}
              />
            }
          >
            <Route path="*" element={null} />
          </Route>
        </Routes>
      </EDNavigationOverlay>
    </NestedBreadcrumbsContextProvider>
  );
};
